import type { PatchCaseUpdateBody } from '@packages/clients';

import { getLogger } from '@packages/core/logging';

import { StsBackendOperation } from '../../../sts-backend-operation';
import { buildCidCvnUpdateBody } from '../../cases/helpers';

const logger = getLogger('techline.cid-cvn');

/** Parameters for saving CID-CVN case details. */
export type SaveCaseParams = {
    /** The case number to update. */
    caseNumber: string;

    /** The CID-CVN form data. */
    formData: {
        /** The vehicle mileage. */
        mileage: string;

        /** The repair order number. */
        repairOrderNumber: string;

        /** The repair order open date. */
        repairOrderOpenDate: string;
    };

    /** The retailer number associated with the case. */
    retailerNumber: string | undefined;

    /** The username making the update. */
    username: string;

    /** The VIN associated with the case. */
    vin: string | undefined;
};

/**
 * Save CID-CVN case details by building the update payload and calling
 * the NorthStar draft case API. Throws an error with a user-facing
 * message if the save fails.
 */
export class SaveCase extends StsBackendOperation {
    /**
     * Build the CID-CVN payload and persist it via the API.
     *
     * @param params - the CID-CVN form data and identity fields
     * @throws {Error} if the save fails, with a user-facing message
     */
    async exec(params: SaveCaseParams): Promise<void> {
        // Build the update payload from the CID-CVN form data
        try {
            const updateBody = buildCidCvnUpdateBody(params) as PatchCaseUpdateBody;

            // Persist the case via the draft case API
            const response = await this.client.updateCase({ updateBody });

            if (!response.success) {
                const errorMessage =
                    response.data?.message || 'We encountered an error while saving your case. Please try again.';
                throw new Error(errorMessage);
            }
        } catch (e) {
            const message = e instanceof Error ? e.message : 'Failed to save case';
            logger.error('Failed to save the CID/CVN case', { error: e });
            throw e instanceof Error ? e : new Error(message);
        }
    }
}
